<template>
  <div
    class="user-activity-details"
    :class="{
      'two-part': swapStyle === 'two-part',
      'three-part': swapStyle === 'three-part',
      'in-card': inCard || inMenu,
      'in-menu': inMenu
    }"
  >
    <div class="activity-column">
      <div class="activity-title">{{ $t('chat_last_active') }}</div>
      <div class="activity-value">{{ getLastActiveAt(lastActive) }}</div>
    </div>

    <div class="activity-column">
      <div class="activity-title">{{ $t('chat_response_frequency') }}</div>
      <div class="activity-value">
        {{ responseFrequency ? `${responseFrequency}%` : '-' }}
      </div>
    </div>

    <div class="activity-column">
      <div class="activity-title">{{ $t('chat_response_time') }}</div>
      <div class="activity-value">{{ getAvgResponseTime(responseTime) }}</div>
    </div>
  </div>
</template>

<script>
import { lbApiBeta } from '../../../utils/axiosConfig';

// The last answer per ad. A card mounted again (the menu mounts a new one
// each time it opens) shows it at once while it asks again.
const latestActivity = {};

export default {
  props: {
    propositionId: {
      type: String,
      required: true
    },
    swapStyle: {
      type: String,
      required: false,
      default: null // two-part or three-part
    },
    // On an ad's user card: three across, a size up, as in the menu.
    inCard: {
      type: Boolean,
      default: false
    },
    // Your own numbers on the menu's profile card.
    inMenu: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      lastActive: null,
      responseFrequency: null,
      responseTime: null
    };
  },

  watch: {
    async propositionId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.init();
      }
    }
  },

  created() {
    this.init();
  },

  methods: {
    init() {
      const { propositionId } = this;
      this.setActivity(latestActivity[propositionId]);

      // A failed request leaves the row as it is: the menu mounts it for
      // everyone with an ad.
      lbApiBeta
        .get(`/activity/${propositionId}`)
        .then(res => {
          if (!res.data) {
            return;
          }

          latestActivity[propositionId] = res.data;
          if (propositionId === this.propositionId) {
            this.setActivity(res.data);
          }
        })
        .catch(() => {});
    },

    setActivity(activity) {
      this.lastActive = activity ? activity.lastLogin : null;
      this.responseFrequency = activity ? activity.responseFrequency : null;
      this.responseTime = activity ? activity.avgResponseTime : null;
    },

    getLastActiveAt(timeStamp) {
      if (!timeStamp) {
        return '-';
      }

      // An ad that is not active gets an empty answer, whose lastLogin is
      // .NET's DateTime.MinValue: no data.
      const lastActiveMs = new Date(timeStamp).getTime();
      if (!(lastActiveMs > 0)) {
        return '-';
      }
      const nowMs = new Date().getTime();

      const diff = nowMs - lastActiveMs;

      const hours = (diff / 1000 / 60 / 60).toFixed();
      if (isNaN(hours)) {
        return '-';
      }

      // Short, in the match rows' units ("3h", "5d"; "3Std", "5T" in
      // German): the row sits three across in the menu, where long values
      // broke into a word per line. Under an hour it is just "Nu".
      if (hours < 1) {
        return this.$t('swap_overview_active_now');
      } else if (hours > 24) {
        const days = Number((hours / 24).toFixed());
        return `${days > 30 ? '30+' : days}${this.$t('time_ago_days_short')}`;
      } else {
        return `${hours}${this.$t('time_ago_hours_short')}`;
      }
    },

    getAvgResponseTime() {
      // The API sends null when there is no response time; 0 is none either.
      if (!this.responseTime) {
        return '-';
      }

      const hours = (this.responseTime / 60 / 60).toFixed();
      if (isNaN(hours)) {
        return '-';
      }

      // As short as "Senast aktiv": "1h" under an hour too, then "3h", "1d".
      const hourUnit = this.$t('time_ago_hours_short');
      if (hours < 1) {
        return `1${hourUnit}`;
      } else if (hours < 25) {
        return `${hours}${hourUnit}`;
      } else {
        return `${(hours / 24).toFixed()}${this.$t('time_ago_days_short')}`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.user-activity-details {
  text-align: center;
  margin-top: 10px !important;
  padding: 8px 14px !important;
  border-top: 1px solid rgba(28, 45, 65, 0.08);
  border-bottom: 1px solid rgba(28, 45, 65, 0.08);
  margin-bottom: -1px !important;
}

.user-activity-details.two-part {
  text-align: left;
}

.user-activity-details.three-part {
  text-align: left;
}

.activity-value {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: bold;

  @media ($desktop) {
    margin-top: 4px;
  }
}

.activity-title-icon {
  display: inline-block;
}

.activity-column {
  display: inline-block;
  width: 33%;
  font-size: 0.8rem;
  max-width: 125px;
  vertical-align: top;
  @media ($mobile) {
    width: 100%;
    max-width: 90px;
  }
}

.user-activity-details.three-part .activity-column {
  @media ($mobile) {
    margin-bottom: 5px;
  }
}

.activity-title {
  color: var(--Text-Secondary);
  font-size: 0.6rem;
}

// An ad's user card and the menu's profile card: the three across the card,
// a size up. Each column is as wide as its title on one line and the room
// left is shared. The titles shrink with the card (the row is their
// container) so the longest set, Dutch "Laatst actief / Antwoordfrequentie /
// Reactietijd", keeps to one line in the menu from 340 px phones up, with
// about 5 % to spare at 360 px for browsers that set it wider. Texts wrap
// only between words (sanitize.css breaks inside them); if a title still
// wraps, the columns stay aligned at the top.
.user-activity-details.in-card {
  container-type: inline-size;
  display: flex;
  gap: 6px;
  word-break: normal;

  .activity-column {
    flex: 1 1 auto;
    width: auto;
    max-width: none;
  }

  .activity-title {
    font-size: 12px;
    font-size: clamp(10px, calc((100cqi - 12px) / 21), 12px);
  }

  .activity-value {
    margin-top: 2px;
    font-size: 14px;
    font-weight: 600;
  }
}

// The menu: under a line inset like the card's content, no line below.
.user-activity-details.in-menu {
  margin: 0 14px !important;
  padding: 12px 0 14px !important;
  border-bottom: none;
}
</style>
